import React, { useState } from "react";
import axios from "axios";
import {
  Handshake, Loader2, AlertTriangle, IndianRupee, Building2,
  Home, FileText, User, Copy, Check
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { API } from "../App";
import { toast } from "sonner";

const PAYMENT_PLANS = [
  { value: "CLP", label: "Construction Linked Plan (CLP)" },
  { value: "TLP", label: "Time Linked Plan (TLP)" },
  { value: "down_payment", label: "Down Payment Plan" },
  { value: "flexi", label: "Flexi Plan" },
  { value: "subvention", label: "Subvention Scheme" },
  { value: "other", label: "Other" },
];

const FREEBIE_OPTIONS = [
  "Parking", "Modular Kitchen", "Stamp Duty Waiver", "Registration Waiver",
  "Club Membership", "Maintenance Free Period", "Flooring Upgrade",
  "GST Absorbed", "AC Units", "Furniture Package",
];

export default function NegotiationForm({ leadId, lead, open, onClose, onComplete }) {
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    quoted_price: "",
    offered_price: "",
    payment_plan: "",
    unit_preference: "",
    freebies: [],
    buyer_concerns: "",
    buyer_budget_stated: "",
    decision_timeline: "",
    decision_makers: "",
    competing_options: "",
    notes: "",
  });

  // Pre-fill from lead data when dialog opens
  React.useEffect(() => {
    if (open && lead) {
      setForm(prev => ({
        ...prev,
        quoted_price: lead.budget_max ? String(lead.budget_max) : "",
        buyer_budget_stated: lead.budget_max
          ? `${(lead.budget_min / 100000).toFixed(0)}L - ${(lead.budget_max / 100000).toFixed(0)}L`
          : "",
        unit_preference: lead.configuration || "",
      }));
    }
  }, [open, lead]);

  const toggleFreebie = (freebie) => {
    setForm(prev => ({
      ...prev,
      freebies: prev.freebies.includes(freebie)
        ? prev.freebies.filter(f => f !== freebie)
        : [...prev.freebies, freebie],
    }));
  };

  const formatINR = (val) => {
    const num = Number(val);
    if (!num) return "";
    if (num >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(1)} L`;
    return num.toLocaleString("en-IN");
  };

  const handleSubmit = async () => {
    if (!form.quoted_price || !form.offered_price) {
      toast.error("Quoted price and offered price are required");
      return;
    }
    setLoading(true);
    try {
      // Step 1: Update stage to negotiation (only if not already in negotiation/negotiation_scheduled)
      const currentStage = lead?.stage || "";
      if (!currentStage.includes("negotiation")) {
        await axios.put(`${API}/leads/${leadId}`, { stage: "negotiation" });
      }

      // Step 2: Create negotiation record
      await axios.post(`${API}/negotiations`, {
        lead_id: leadId,
        initial_quoted_price: Number(form.quoted_price),
        offered_price: Number(form.offered_price),
        payment_plan: form.payment_plan,
        unit_id: form.unit_preference,
        freebies: form.freebies,
        notes: [
          form.notes,
          form.buyer_concerns ? `Buyer Concerns: ${form.buyer_concerns}` : "",
          form.decision_timeline ? `Timeline: ${form.decision_timeline}` : "",
          form.decision_makers ? `Decision Makers: ${form.decision_makers}` : "",
          form.competing_options ? `Competing: ${form.competing_options}` : "",
        ].filter(Boolean).join(". "),
      });

      // Step 3: Log interaction
      await axios.post(`${API}/interactions`, {
        lead_id: leadId,
        type: "negotiation",
        content: `Negotiation started. Quoted: ₹${formatINR(form.quoted_price)}, Offered: ₹${formatINR(form.offered_price)}. ${form.payment_plan ? `Plan: ${form.payment_plan}.` : ""} ${form.freebies.length ? `Freebies: ${form.freebies.join(", ")}.` : ""} ${form.notes || ""}`.trim(),
      });

      toast.success("Negotiation started");
      if (onComplete) onComplete();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start negotiation");
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    toast.error("Please fill the negotiation form to proceed");
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) resetAndClose(); }}>
      <DialogContent
        className="sm:max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="negotiation-form-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Manrope']">
            <div className="w-8 h-8 bg-fuchsia-100 rounded-lg flex items-center justify-center">
              <Handshake className="w-4 h-4 text-fuchsia-600" />
            </div>
            Start Negotiation — {lead?.name || "Lead"}
          </DialogTitle>
          <p className="text-sm text-slate-500">Capture negotiation details before entering the deal stage</p>
        </DialogHeader>

        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          This form is mandatory to move the lead to Negotiation stage
        </div>

        {/* Pre-filled Lead Context */}
        <div className="flex flex-wrap gap-2 text-xs">
          {lead?.project_interest && <Badge variant="outline" className="gap-1"><Building2 className="w-3 h-3" />{lead.project_interest}</Badge>}
          {lead?.configuration && <Badge variant="outline" className="gap-1"><Home className="w-3 h-3" />{lead.configuration}</Badge>}
          {lead?.budget_max && <Badge variant="outline" className="gap-1"><IndianRupee className="w-3 h-3" />Budget: {formatINR(lead.budget_min)}-{formatINR(lead.budget_max)}</Badge>}
          {lead?.score && <Badge variant="outline" className="capitalize">{lead.score}</Badge>}
          {lead?.awareness_stage && <Badge variant="outline" className="capitalize">{lead.awareness_stage?.replace(/_/g, " ")}</Badge>}
        </div>

        <div className="space-y-4">
          {/* Pricing Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Quoted Price (₹) *</Label>
              <Input
                type="number"
                placeholder="e.g., 7500000"
                value={form.quoted_price}
                onChange={(e) => setForm(f => ({ ...f, quoted_price: e.target.value }))}
                className="mt-1"
                data-testid="quoted-price-input"
              />
              {form.quoted_price && <p className="text-xs text-slate-400 mt-0.5">₹{formatINR(form.quoted_price)}</p>}
            </div>
            <div>
              <Label className="text-sm font-medium">Offered Price (₹) *</Label>
              <Input
                type="number"
                placeholder="e.g., 7200000"
                value={form.offered_price}
                onChange={(e) => setForm(f => ({ ...f, offered_price: e.target.value }))}
                className="mt-1"
                data-testid="offered-price-input"
              />
              {form.offered_price && <p className="text-xs text-slate-400 mt-0.5">₹{formatINR(form.offered_price)}</p>}
              {form.quoted_price && form.offered_price && Number(form.quoted_price) > 0 && (
                <p className="text-xs text-fuchsia-600 mt-0.5 font-medium">
                  Discount: {(((Number(form.quoted_price) - Number(form.offered_price)) / Number(form.quoted_price)) * 100).toFixed(1)}%
                </p>
              )}
            </div>
          </div>

          {/* Payment Plan & Unit */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Payment Plan</Label>
              <Select value={form.payment_plan} onValueChange={(v) => setForm(f => ({ ...f, payment_plan: v }))}>
                <SelectTrigger className="mt-1" data-testid="payment-plan-select">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_PLANS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Unit / Configuration</Label>
              <Input
                placeholder="e.g., 2BHK A-401"
                value={form.unit_preference}
                onChange={(e) => setForm(f => ({ ...f, unit_preference: e.target.value }))}
                className="mt-1"
                data-testid="unit-input"
              />
            </div>
          </div>

          {/* Freebies */}
          <div>
            <Label className="text-sm font-medium">Freebies Discussed</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {FREEBIE_OPTIONS.map((fb) => (
                <button
                  key={fb}
                  onClick={() => toggleFreebie(fb)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                    form.freebies.includes(fb)
                      ? "bg-fuchsia-100 border-fuchsia-300 text-fuchsia-800"
                      : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
                  }`}
                  data-testid={`freebie-${fb.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {form.freebies.includes(fb) ? "✓ " : ""}{fb}
                </button>
              ))}
            </div>
          </div>

          {/* Buyer Intelligence */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Buyer's Stated Budget</Label>
              <Input
                placeholder="e.g., 65-70L"
                value={form.buyer_budget_stated}
                onChange={(e) => setForm(f => ({ ...f, buyer_budget_stated: e.target.value }))}
                className="mt-1"
                data-testid="buyer-budget-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Decision Timeline</Label>
              <Input
                placeholder="e.g., Within 2 weeks"
                value={form.decision_timeline}
                onChange={(e) => setForm(f => ({ ...f, decision_timeline: e.target.value }))}
                className="mt-1"
                data-testid="timeline-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Decision Makers</Label>
              <Input
                placeholder="e.g., Husband + Father-in-law"
                value={form.decision_makers}
                onChange={(e) => setForm(f => ({ ...f, decision_makers: e.target.value }))}
                className="mt-1"
                data-testid="decision-makers-input"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Competing Options</Label>
              <Input
                placeholder="e.g., Godrej project nearby"
                value={form.competing_options}
                onChange={(e) => setForm(f => ({ ...f, competing_options: e.target.value }))}
                className="mt-1"
                data-testid="competing-input"
              />
            </div>
          </div>

          {/* Concerns & Notes */}
          <div>
            <Label className="text-sm font-medium">Buyer Concerns / Objections</Label>
            <Input
              placeholder="e.g., Price too high, wants east-facing"
              value={form.buyer_concerns}
              onChange={(e) => setForm(f => ({ ...f, buyer_concerns: e.target.value }))}
              className="mt-1"
              data-testid="concerns-input"
            />
          </div>

          <div>
            <Label className="text-sm font-medium">Notes</Label>
            <Textarea
              placeholder="Any additional context..."
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
              className="mt-1 min-h-[60px]"
              data-testid="negotiation-notes-input"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-fuchsia-600 hover:bg-fuchsia-700"
            data-testid="start-negotiation-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Handshake className="w-4 h-4 mr-2" />}
            {loading ? "Starting Negotiation..." : "Start Negotiation"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
